import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import apiClient from "../api/apiClient";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import SecureLogger from "../utils/secureLogger";
import type {
  DailyRecapBriefing,
  DailyRecapBriefingChoice,
  DailyRecapBriefingPreference,
} from "../types/dailyRecapBriefing";
import DailyRecapBriefingModal from "./DailyRecapBriefingModal";

interface DailyRecapBriefingManagerProps {
  blocked?: boolean;
}

interface ClaimResponse {
  eligible: boolean;
  reason: string;
  preference: DailyRecapBriefingPreference;
  briefing: DailyRecapBriefing | null;
}

const DEFAULT_PREFERENCE: DailyRecapBriefingPreference = {
  mode: "daily",
  choice: "daily",
  pause_days: null,
  paused_until: null,
  first_presented_at: null,
  can_manage: false,
};

const getBusinessDateKey = (reference = new Date()) => {
  const parts = new Intl.DateTimeFormat("fr-FR", {
    timeZone: "Africa/Abidjan",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(reference);
  const values: Record<string, string> = {};
  parts.forEach((part) => {
    if (part.type !== "literal") values[part.type] = part.value;
  });
  return `${values.year}-${values.month}-${values.day}`;
};

const isElementVisible = (element: Element) => {
  if (!(element instanceof HTMLElement)) return false;

  if (element.closest('[aria-hidden="true"], [hidden], [inert]')) {
    return false;
  }

  let currentElement: HTMLElement | null = element;
  while (currentElement) {
    const style = window.getComputedStyle(currentElement);
    const opacity = Number.parseFloat(style.opacity);
    if (
      style.display === "none" ||
      style.visibility === "hidden" ||
      (Number.isFinite(opacity) && opacity === 0)
    ) {
      return false;
    }
    currentElement = currentElement.parentElement;
  }

  const rect = element.getBoundingClientRect();
  return (
    element.getClientRects().length > 0 &&
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.top < window.innerHeight &&
    rect.left < window.innerWidth
  );
};

export const hasVisibleBlockingOverlay = () => {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return false;
  }

  return Array.from(
    document.querySelectorAll(
      '[role="dialog"], .ant-drawer-content-wrapper, .ant-modal-wrap'
    )
  ).some(isElementVisible);
};

const DailyRecapBriefingManager: React.FC<
  DailyRecapBriefingManagerProps
> = ({ blocked = false }) => {
  const { user } = useAuth();
  const { selectedCompany, setSelectedWarehouse } = useSelection();
  const navigate = useNavigate();
  const [briefing, setBriefing] = useState<DailyRecapBriefing | null>(null);
  const [preference, setPreference] =
    useState<DailyRecapBriefingPreference>(DEFAULT_PREFERENCE);
  const [presentationState, setPresentationState] = useState<
    "idle" | "confirming" | "confirmed" | "error"
  >("idle");
  const [pageVisible, setPageVisible] = useState(
    () => typeof document === "undefined" || document.visibilityState === "visible"
  );
  const attemptedScopesRef = useRef(new Set<string>());
  const retryNotBeforeRef = useRef(new Map<string, number>());
  const blockedRef = useRef(blocked);
  const attemptClaimRef = useRef<() => void>(() => undefined);
  const contextGenerationRef = useRef(0);
  const confirmationKeyRef = useRef<string | null>(null);

  const userId = Number(user?.id) || null;
  const subscriptionPortalOnly = Boolean(user?.subscription_portal_only);
  const selectedCompanyId = Number(selectedCompany) || null;
  const selectedCompanyIsAvailable = useMemo(() => {
    if (!selectedCompanyId || !user) return false;
    if (user.is_superadmin) return true;

    return (Array.isArray(user.assigned_warehouses)
      ? user.assigned_warehouses
      : []
    ).some(
      (warehouse) => Number(warehouse?.company_id) === selectedCompanyId
    );
  }, [selectedCompanyId, user]);

  useEffect(() => {
    blockedRef.current = blocked;
    if (!blocked) attemptClaimRef.current();
  }, [blocked]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = document.visibilityState === "visible";
      setPageVisible(isVisible);
      if (isVisible) {
        attemptClaimRef.current();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  useEffect(() => {
    contextGenerationRef.current += 1;
    attemptedScopesRef.current = new Set<string>();
    retryNotBeforeRef.current = new Map<string, number>();
    confirmationKeyRef.current = null;
    setBriefing(null);
    setPreference(DEFAULT_PREFERENCE);
    setPresentationState("idle");
  }, [selectedCompanyId, userId]);

  useEffect(() => {
    if (
      !userId ||
      !selectedCompanyId ||
      !selectedCompanyIsAvailable ||
      subscriptionPortalOnly
    ) {
      return undefined;
    }

    const contextGeneration = contextGenerationRef.current;
    let initialTimer: number | undefined;

    const attemptClaim = async () => {
      const scopeKey = `${userId}:${selectedCompanyId}:${getBusinessDateKey()}`;
      const retryNotBefore = retryNotBeforeRef.current.get(scopeKey) || 0;
      if (
        attemptedScopesRef.current.has(scopeKey) ||
        Date.now() < retryNotBefore ||
        blockedRef.current ||
        !pageVisible ||
        hasVisibleBlockingOverlay()
      ) return;

      attemptedScopesRef.current.add(scopeKey);

      try {
        const response = await apiClient.post<ClaimResponse>(
          "/api/daily-recap-briefing/claim",
          { company_id: selectedCompanyId },
          { skipGlobalLoading: true }
        );

        if (contextGenerationRef.current !== contextGeneration) {
          attemptedScopesRef.current.delete(scopeKey);
          return;
        }
        setPreference(response.data.preference || DEFAULT_PREFERENCE);
        if (response.data.briefing?.notifications?.length) {
          setBriefing(response.data.briefing);
        } else if (response.data.reason === "already_presented_or_claimed") {
          attemptedScopesRef.current.delete(scopeKey);
          retryNotBeforeRef.current.set(scopeKey, Date.now() + 5 * 60_000);
        }
      } catch (_error) {
        attemptedScopesRef.current.delete(scopeKey);
        retryNotBeforeRef.current.set(scopeKey, Date.now() + 60_000);
        SecureLogger.warn("Daily recap briefing claim failed");
      }
    };

    attemptClaimRef.current = () => void attemptClaim();
    initialTimer = window.setTimeout(() => void attemptClaim(), 800);
    const pollTimer = window.setInterval(() => void attemptClaim(), 10_000);

    return () => {
      if (initialTimer !== undefined) window.clearTimeout(initialTimer);
      window.clearInterval(pollTimer);
      attemptClaimRef.current = () => undefined;
    };
  }, [
    selectedCompanyId,
    selectedCompanyIsAvailable,
    pageVisible,
    subscriptionPortalOnly,
    userId,
  ]);

  const confirmPresentation = useCallback(async () => {
    if (!briefing || !selectedCompanyId) return;
    const confirmationKey = `${selectedCompanyId}:${briefing.recap_date}:${briefing.claim_token}`;
    if (confirmationKeyRef.current === confirmationKey) return;

    confirmationKeyRef.current = confirmationKey;
    setPresentationState("confirming");
    try {
      const response = await apiClient.post<{
        preference: DailyRecapBriefingPreference;
      }>(
        "/api/daily-recap-briefing/presented",
        {
          company_id: selectedCompanyId,
          recap_date: briefing.recap_date,
          claim_token: briefing.claim_token,
        },
        { skipGlobalLoading: true }
      );
      setPreference(response.data.preference || DEFAULT_PREFERENCE);
      setPresentationState("confirmed");
    } catch (error) {
      confirmationKeyRef.current = null;
      setPresentationState("error");
      SecureLogger.warn("Daily recap briefing presentation was not confirmed");
    }
  }, [briefing, selectedCompanyId]);

  useEffect(() => {
    if (briefing && pageVisible) void confirmPresentation();
  }, [briefing, confirmPresentation, pageVisible]);

  const changePreference = useCallback(
    async (choice: DailyRecapBriefingChoice) => {
      if (!selectedCompanyId || presentationState !== "confirmed") {
        throw new Error("La présentation du briefing doit être confirmée");
      }

      const response = await apiClient.put<{
        preference: DailyRecapBriefingPreference;
      }>(
        "/api/daily-recap-briefing/preference",
        { company_id: selectedCompanyId, choice },
        { skipGlobalLoading: true }
      );
      setPreference(response.data.preference);
      return response.data.preference;
    },
    [presentationState, selectedCompanyId]
  );

  const closeBriefing = useCallback(() => {
    if (presentationState === "error") void confirmPresentation();
    setBriefing(null);
  }, [confirmPresentation, presentationState]);

  const openDestination = useCallback(
    (warehouseId: number, url: string) => {
      const destination = /^\/(?!\/)/.test(String(url || "").trim())
        ? String(url).trim()
        : "/rapport";
      setSelectedWarehouse(warehouseId);
      setBriefing(null);
      navigate(destination);
    },
    [navigate, setSelectedWarehouse]
  );

  if (!briefing) return null;

  const firstName = String(user?.name || "")
    .trim()
    .split(/\s+/)[0];

  return (
    <DailyRecapBriefingModal
      open
      briefing={briefing}
      firstName={firstName}
      preference={preference}
      presentationConfirmed={presentationState === "confirmed"}
      presentationConfirming={presentationState === "confirming"}
      onClose={closeBriefing}
      onNavigate={openDestination}
      onPreferenceChange={changePreference}
      onRetryPresentation={() => void confirmPresentation()}
    />
  );
};

export default DailyRecapBriefingManager;
